import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/UserList.css';

const UserList = ({ onLogout }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({ id: '', email: '', password: '' });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  console.log('Rendering UserList page');

  const fetchUsers = async () => {
    try {
      const response = await fetch('/api/users');
      if (!response.ok) throw new Error('Failed to fetch users');
      const data = await response.json();
      setUsers(data);
    } catch (err) {
      setError('Error fetching users: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = isEditing ? `/api/users/${formData.id}` : '/api/users';
      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) throw new Error('Failed to save user');

      // Reset form and refresh list
      setFormData({ id: '', email: '', password: '' });
      setIsEditing(false);
      fetchUsers();
    } catch (err) {
      setError('Error saving user: ' + err.message);
    }
  };

  const handleEdit = (user) => {
    setFormData({ id: user.id, email: user.email, password: '' });
    setIsEditing(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      const response = await fetch(`/api/users/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete user');

      fetchUsers();
    } catch (err) {
      setError('Error deleting user: ' + err.message);
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="dashboard-layout">
      <aside className="sidebar">
        <div className="sidebar-logo">
          <img src="/logo.png" alt="Pricol Logo" />
        </div>
        <nav className="sidebar-nav">
          <Link to="/dashboard" className="sidebar-link">Dashboard</Link>
          <Link to="/users" className="sidebar-link">Users</Link>
          <Link to="/employees" className="sidebar-link">Employees</Link>
        </nav>
        <button className="sidebar-logout" onClick={() => onLogout(navigate)}>Logout</button>
      </aside>
      <main className="dashboard-main">
        <div className="user-list-header">
          <h2>User Management</h2>
        </div>
        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="user-form">
          <h3>{isEditing ? 'Edit User' : 'Add New User'}</h3>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">
              {isEditing ? 'Password (leave blank to keep current)' : 'Password'}
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              required={!isEditing}
            />
          </div>
          <div className="form-buttons">
            <button type="submit">{isEditing ? 'Update' : 'Add'}</button>
            {isEditing && (
              <button
                type="button"
                onClick={() => {
                  setFormData({ id: '', email: '', password: '' });
                  setIsEditing(false);
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </form>

        <div className="user-table-container">
          <table className="user-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Email</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>{user.email}</td>
                  <td>
                    <button onClick={() => handleEdit(user)} className="edit-btn">
                      Edit
                    </button>
                    <button onClick={() => handleDelete(user.id)} className="delete-btn">
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
};

export default UserList;